import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, Link, useSearchParams } from "react-router-dom";
import CommentModal from "../components/common/CommentModal";
import ActionMenu from "../components/common/ActionMenu";
import {
  MdSchedule,
  MdAdd,
  MdEdit,
  MdDelete,
  MdAssignment,
  MdComment,
  MdSearch,
  MdArrowUpward,
  MdArrowDownward,
  MdTask,
  MdFilterAlt,
  MdClose,
  MdPerson,
  MdLabel,
  MdDateRange,
  MdClear,
  MdVisibility,
  MdAttachFile,
  MdWhatsapp,
  MdCheckCircle,
  MdFileDownload,
} from "react-icons/md";
import api from "../services/api";
import toast from "react-hot-toast";
import {
  insertMentionAtCursor,
  toMentionDisplayText,
  parseMentionsOrdered,
  toBackendMentionFormat,
} from "../utils/mentionUtils";
import { canMarkTaskComplete } from "../utils/taskPermissions";
import { handlePendingDoneRequestResponse } from "../utils/taskUpdateHelpers";
import { format, parseISO, startOfDay, endOfDay, isBefore } from "date-fns";
import { useAuth } from "../context/AuthContext";

const API_URL =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_URL || window.location.origin
    : "http://localhost:5000";
const COLORS = [
  "#6366f1",
  "#f59e0b",
  "#10b981",
  "#ef4444",
  "#3b82f6",
  "#8b5cf6",
];
const initials = (name) =>
  name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
const aColor = (name) => COLORS[(name?.charCodeAt(0) || 0) % COLORS.length];

const ADMIN_TASK_TABS = [
  "all",
  "self",
  "done",
  "assigned",
  "coassigned",
  "recurring",
];

/** Query string without leading `?` */
const parseAdminTasksFromSearch = (qs) => {
  const raw = (qs || "").startsWith("?") ? qs.slice(1) : qs || "";
  const p = new URLSearchParams(raw);
  const tab = p.get("tab");
  const pg = parseInt(p.get("page") || "1", 10);
  return {
    taskTab: tab && ADMIN_TASK_TABS.includes(tab) ? tab : "all",
    search: p.get("q") ?? "",
    fStatus: p.get("status") ?? "",
    fPriority: p.get("prio") ?? "",
    fCreatedBy: p.get("by") ?? "",
    fAssignedTo: p.get("assign") ?? "",
    fCoAssignedTo: p.get("coby") ?? "",
    fTag: p.get("tag") ?? "",
    createdFrom: p.get("cf") ?? "",
    createdTo: p.get("ct") ?? "",
    deadlineFrom: p.get("df") ?? "",
    deadlineTo: p.get("dt") ?? "",
    quickFilter: p.get("quick") ?? "",
    showAdvanced: p.get("adv") === "1",
    page: !Number.isNaN(pg) && pg >= 1 ? pg : 1,
  };
};

/** Not done and deadline calendar day is before today */
const isTaskOverdue = (task) =>
  Boolean(
    task?.deadline &&
      task.status !== "done" &&
      isBefore(
        startOfDay(new Date(task.deadline)),
        startOfDay(new Date()),
      ),
  );

const ADMIN_TASK_TAB_LABELS = {
  all: "All Tasks",
  self: "Self Created Tasks",
  done: "Done Tasks",
  assigned: "Tasks Assigned to Others",
  coassigned: "Co-Assigned Tasks",
  recurring: "Recurring Tasks",
};

const QUICK_FILTER_LABELS = {
  todo: "To Do",
  "in-progress": "In Progress",
  review: "Review",
  done: "Done",
  "completed-today": "Completed Today",
  "due-today": "Due Today",
  overdue: "Overdue",
  "due-within-days": "Due Within 3 Days",
  "my-pending": "My Pending",
};

const STATUS_LABELS = {
  todo: "To Do",
  "in-progress": "In Progress",
  review: "Review",
  done: "Done",
};

const exportToCsv = (filename, rows) => {
  const encode = (val) => {
    if (val === null || val === undefined) return "";
    const str = String(val).replace(/"/g, '""');
    return `"${str}"`;
  };
  const csv = rows.map((r) => r.map(encode).join(",")).join("\r\n");
  const BOM = "\uFEFF";
  const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

const serializeAdminTasksQuery = (x) => {
  const p = new URLSearchParams();
  if (x.taskTab !== "all") p.set("tab", x.taskTab);
  if (x.search) p.set("q", x.search);
  if (x.fStatus) p.set("status", x.fStatus);
  if (x.fPriority) p.set("prio", x.fPriority);
  if (x.fCreatedBy) p.set("by", x.fCreatedBy);
  if (x.fAssignedTo) p.set("assign", x.fAssignedTo);
  if (x.fCoAssignedTo) p.set("coby", x.fCoAssignedTo);
  if (x.fTag) p.set("tag", x.fTag);
  if (x.createdFrom) p.set("cf", x.createdFrom);
  if (x.createdTo) p.set("ct", x.createdTo);
  if (x.deadlineFrom) p.set("df", x.deadlineFrom);
  if (x.deadlineTo) p.set("dt", x.deadlineTo);
  if (x.quickFilter) p.set("quick", x.quickFilter);
  if (x.showAdvanced) p.set("adv", "1");
  if (x.page > 1) p.set("page", String(x.page));
  return p.toString();
};

/* ── Real-photo-aware user avatar ─────────────────────────── */
const UserAvatar = ({ user, size = 30 }) => {
  const src = user?.avatar
    ? user.avatar.startsWith("http")
      ? user.avatar
      : `${API_URL}${user.avatar}`
    : null;
  return (
    <div
      className="avatar"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.3,
        background: src ? "transparent" : aColor(user?.name),
        flexShrink: 0,
      }}
    >
      {src ? (
        <img
          src={src}
          alt={user?.name}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "50%",
          }}
          onError={(e) => {
            e.target.style.display = "none";
            e.target.parentNode.textContent = initials(user?.name);
          }}
        />
      ) : (
        initials(user?.name)
      )}
    </div>
  );
};

/* ── Sortable header ──────────────────────────────────────── */
const SortTH = ({ label, field, sortBy, sortDir, onSort }) => (
  <th
    onClick={() => onSort(field)}
    style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      {label}
      {sortBy === field ? (
        sortDir === "asc" ? (
          <MdArrowUpward size={13} color="var(--primary)" />
        ) : (
          <MdArrowDownward size={13} color="var(--primary)" />
        )
      ) : (
        <MdArrowUpward size={13} color="var(--border2)" />
      )}
    </div>
  </th>
);

/* ── Task modal ───────────────────────────────────────────── */
const TaskModal = ({ task, users, currentUserId, onClose, onSaved }) => {
  const blank = {
    title: "",
    description: "",
    assignedTo: [],
    coAssignedTo: "",
    status: "todo",
    priority: "medium",
    deadline: "",
    tags: "",
  };
  const [form, setForm] = useState(
    task
      ? {
          title: task.title,
          description: toMentionDisplayText(task.description || ""),
          assignedTo: Array.isArray(task.assignedUsers) && task.assignedUsers.length
            ? task.assignedUsers.map((u) => (u?._id ? u._id : u)).filter(Boolean)
            : task.assignedTo?._id
              ? [task.assignedTo._id]
              : [],
          coAssignedTo: task.coAssignedTo?._id || "",
          status: task.status,
          priority: task.priority,
          deadline: task.deadline ? task.deadline.slice(0, 10) : "",
          tags: Array.isArray(task.tags) ? task.tags.join(", ") : "",
        }
      : blank,
  );
  const [saving, setSaving] = useState(false);
  const [assignSearch, setAssignSearch] = useState("");
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionStart, setMentionStart] = useState(-1);
  const [mentionSearch, setMentionSearch] = useState("");
  const descRef = React.useRef(null);
  const descMentionListRef = React.useRef([]);
  const createAttachInputRef = React.useRef(null);
  const [pendingFiles, setPendingFiles] = useState([]);

  React.useEffect(() => {
    if (task?.description)
      descMentionListRef.current = parseMentionsOrdered(task.description);
    else descMentionListRef.current = [];
  }, [task?._id, task?.description]);

  React.useEffect(() => {
    if (task) setPendingFiles([]);
  }, [task?._id, task]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.deadline) {
      toast.error("Deadline is required");
      return;
    }
    if (
      task &&
      form.status === "done" &&
      task.status !== "done" &&
      !canMarkTaskComplete(currentUserId, task)
    ) {
      toast.error("Only the task creator or assigned users can mark this task as complete");
      return;
    }
    setSaving(true);
    try {
      const descriptionForApi = toBackendMentionFormat(
        form.description,
        descMentionListRef.current,
      );
      const payload = {
        ...form,
        description: descriptionForApi,
        tags:
          typeof form.tags === "string"
            ? form.tags
                .split(",")
                .map((t) => t.trim())
                .filter(Boolean)
            : form.tags,
        assignedUsers: form.assignedTo,
        assignedTo: form.assignedTo[0] || undefined,
        coAssignedTo: form.coAssignedTo || null,
        deadline: form.deadline,
      };
      if (task) {
        const { data } = await api.put(`/tasks/${task._id}`, payload);
        if (!handlePendingDoneRequestResponse(data, toast)) {
          toast.success("Task updated");
        }
      } else {
        const { data } = await api.post("/tasks", payload);
        const createdId = data.data?._id;
        const files = pendingFiles;
        const uploadErrors = [];
        if (files.length > 0 && createdId) {
          for (const file of files) {
            try {
              const fd = new FormData();
              fd.append("file", file);
              await api.post(`/tasks/${createdId}/attachments`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
              });
            } catch {
              uploadErrors.push(file.name);
            }
          }
        }
        if (uploadErrors.length === 0) {
          toast.success(
            files.length > 0
              ? "Task created with attachments"
              : "Task created",
          );
        } else if (uploadErrors.length === files.length) {
          toast.success("Task created");
          toast.error(
            "Attachments failed to upload. Add files from task details.",
          );
        } else {
          toast.success("Task created");
          toast.error(
            `Some files failed: ${uploadErrors.join(", ")}. Add the rest from task details.`,
          );
        }
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error");
    } finally {
      setSaving(false);
    }
  };

  const F = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const showDoneStatusOption =
    !task ||
    canMarkTaskComplete(currentUserId, task) ||
    (task.status || "") === "done";

  const handleDescChange = (e) => {
    const val = e.target.value;
    const pos = e.target.selectionStart ?? val.length;
    setForm({ ...form, description: val });
    const beforeCursor = val.slice(0, pos);
    const atIdx = beforeCursor.lastIndexOf("@");
    if (atIdx !== -1 && (atIdx === 0 || /\s/.test(val[atIdx - 1]))) {
      setMentionStart(atIdx);
      setMentionSearch(beforeCursor.slice(atIdx + 1).toLowerCase());
      setMentionOpen(true);
    } else {
      setMentionOpen(false);
    }
  };

  const handleSelectDescMention = (u) => {
    const cursorPos =
      descRef.current?.selectionStart ?? form.description.length;
    const countBefore = (
      form.description.slice(0, mentionStart).match(/@/g) || []
    ).length;
    descMentionListRef.current.splice(countBefore, 0, {
      userId: u._id,
      displayName: u.name,
    });
    const { newValue, newPos } = insertMentionAtCursor(
      form.description,
      mentionStart,
      cursorPos,
      u._id,
      u.name,
    );
    setForm({ ...form, description: newValue });
    setMentionOpen(false);
    setTimeout(() => {
      descRef.current?.focus();
      descRef.current?.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const mentionFiltered = mentionSearch
    ? users.filter((u) => (u.name || "").toLowerCase().includes(mentionSearch))
    : users;
  const assignFiltered = assignSearch
    ? users.filter((u) =>
        `${u.name || ""} ${u.email || ""}`.toLowerCase().includes(assignSearch.toLowerCase()),
      )
    : users;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{task ? "Edit Task" : "Create Task"}</h3>
          <button className="btn-icon" onClick={onClose}>
            ✕
          </button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Title *</label>
              <input
                className="form-control"
                value={form.title}
                onChange={F("title")}
                placeholder="Task title"
                required
              />
            </div>
            <div className="form-group" style={{ position: "relative" }}>
              <label className="form-label">Description</label>
              <textarea
                ref={descRef}
                className="form-control"
                rows={3}
                value={form.description}
                onChange={handleDescChange}
                onBlur={() => setTimeout(() => setMentionOpen(false), 200)}
                placeholder="Optional description (type @ to mention a user)"
              />
              {mentionOpen && mentionFiltered.length > 0 && (
                <ul
                  className="mention-dropdown mention-dropdown-below"
                  role="listbox"
                >
                  {mentionFiltered.map((u) => (
                    <li
                      key={u._id}
                      role="option"
                      className="mention-dropdown-item"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSelectDescMention(u);
                      }}
                    >
                      <UserAvatar user={u} size={28} />
                      <span>{u.name}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 14,
              }}
            >
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Assign To (Multiple)</label>
                <div className="form-control" style={{ maxHeight: 190, overflow: "auto" }}>
                  <input
                    className="form-control"
                    style={{ marginBottom: 8 }}
                    placeholder="Search user..."
                    value={assignSearch}
                    onChange={(e) => setAssignSearch(e.target.value)}
                  />
                  {assignFiltered.map((u) => {
                    const checked = form.assignedTo.includes(u._id);
                    return (
                      <label
                        key={u._id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "6px 2px",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setForm((f) => {
                              const next = isChecked
                                ? [...f.assignedTo, u._id]
                                : f.assignedTo.filter((id) => id !== u._id);
                              return {
                                ...f,
                                assignedTo: [...new Set(next)],
                                coAssignedTo:
                                  f.coAssignedTo && next.includes(f.coAssignedTo)
                                    ? ""
                                    : f.coAssignedTo,
                              };
                            });
                          }}
                        />
                        <span>
                          {u.name}
                          {u.organization?.name ? ` · ${u.organization.name}` : ""}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">
                  Co-Assign To
                  <span
                    style={{
                      marginLeft: 6,
                      fontSize: "0.68rem",
                      fontWeight: 500,
                      color: "var(--text-muted)",
                      textTransform: "none",
                      letterSpacing: 0,
                    }}
                  >
                    (assistant)
                  </span>
                </label>
                <select
                  className="form-control"
                  value={form.coAssignedTo}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((f) => ({
                      ...f,
                      coAssignedTo: v,
                      assignedTo: f.assignedTo === v ? "" : f.assignedTo,
                    }));
                  }}
                >
                  <option value="">— None —</option>
                  {users
                    .filter((u) => !form.assignedTo.includes(u._id))
                    .map((u) => (
                      <option key={u._id} value={u._id}>
                        {u.name}
                        {u.organization?.name
                          ? ` · ${u.organization.name}`
                          : ""}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="form-control"
                  value={form.status}
                  onChange={F("status")}
                  title={
                    task &&
                    !canMarkTaskComplete(currentUserId, task) &&
                    (task.status || "") !== "done"
                      ? "Only the creator or assigned users can mark this task complete"
                      : undefined
                  }
                >
                  <option value="todo">To Do</option>
                  <option value="in-progress">In Progress</option>
                  <option value="review">Review</option>
                  {showDoneStatusOption && <option value="done">Done</option>}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Priority</label>
                <select
                  className="form-control"
                  value={form.priority}
                  onChange={F("priority")}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Deadline</label>
              <input
                className="form-control"
                type="date"
                value={form.deadline}
                onChange={F("deadline")}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Tags (comma-separated)</label>
              <input
                className="form-control"
                value={form.tags}
                onChange={F("tags")}
                placeholder="frontend, bug, design"
              />
            </div>
            {!task && (
              <div className="form-group">
                <label className="form-label">
                  Related files{" "}
                  <span
                    style={{
                      fontWeight: 400,
                      color: "var(--text-muted)",
                    }}
                  >
                    (optional, max 2)
                  </span>
                </label>
                <input
                  ref={createAttachInputRef}
                  type="file"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const picked = Array.from(e.target.files || []);
                    setPendingFiles((prev) => {
                      const next = [...prev];
                      for (const f of picked) {
                        if (next.length >= 2) break;
                        next.push(f);
                      }
                      if (prev.length + picked.length > 2) {
                        toast.error("You can attach at most 2 files.");
                      }
                      return next;
                    });
                    e.target.value = "";
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={pendingFiles.length >= 2}
                    onClick={() => createAttachInputRef.current?.click()}
                  >
                    <MdAttachFile
                      size={16}
                      style={{ verticalAlign: "text-bottom" }}
                    />{" "}
                    Add file
                  </button>
                  <span
                    style={{
                      fontSize: "0.75rem",
                      color: "var(--text-muted)",
                    }}
                  >
                    PDF, images, Word, Excel, CSV, TXT, ZIP · 20 MB each
                  </span>
                </div>
                {pendingFiles.length > 0 && (
                  <ul
                    style={{
                      margin: "8px 0 0",
                      paddingLeft: 18,
                      fontSize: "0.85rem",
                    }}
                  >
                    {pendingFiles.map((f, i) => (
                      <li
                        key={`${f.name}-${i}-${f.size}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span title={f.name}>{f.name}</span>
                        <button
                          type="button"
                          className="btn-icon"
                          aria-label="Remove file"
                          onClick={() =>
                            setPendingFiles((prev) =>
                              prev.filter((_, j) => j !== i),
                            )
                          }
                        >
                          ✕
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving…" : task ? "Update Task" : "Create Task"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Assign modal ─────────────────────────────────────────── */
const AssignModal = ({ task, users, onClose, onSaved }) => {
  const [userIds, setUserIds] = useState(
    Array.isArray(task.assignedUsers) && task.assignedUsers.length
      ? task.assignedUsers.map((u) => (u?._id ? u._id : u)).filter(Boolean)
      : task.assignedTo?._id
        ? [task.assignedTo._id]
        : [],
  );
  const [saving, setSaving] = useState(false);
  const [assignSearch, setAssignSearch] = useState("");
  const assignFiltered = assignSearch
    ? users.filter((u) =>
        `${u.name || ""} ${u.email || ""}`.toLowerCase().includes(assignSearch.toLowerCase()),
      )
    : users;

  const assign = async () => {
    setSaving(true);
    try {
      await api.put(`/tasks/${task._id}`, {
        assignedUsers: userIds,
        assignedTo: userIds[0] || null,
      });
      toast.success("Task assigned");
      onSaved();
      onClose();
    } catch {
      toast.error("Failed to assign");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 420 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>Assign Task</h3>
          <button className="btn-icon" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body">
          <p
            style={{
              fontSize: "0.875rem",
              color: "var(--text-muted)",
              marginBottom: 16,
            }}
          >
            Assigning:{" "}
            <strong style={{ color: "var(--text)" }}>{task.title}</strong>
          </p>
          <div className="form-group">
            <label className="form-label">Assign To (Multiple)</label>
            <div className="form-control" style={{ maxHeight: 220, overflow: "auto" }}>
              <input
                className="form-control"
                style={{ marginBottom: 8 }}
                placeholder="Search user..."
                value={assignSearch}
                onChange={(e) => setAssignSearch(e.target.value)}
              />
              {assignFiltered.map((u) => {
                const checked = userIds.includes(u._id);
                return (
                  <label
                    key={u._id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 2px",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        setUserIds((prev) =>
                          isChecked
                            ? [...new Set([...prev, u._id])]
                            : prev.filter((id) => id !== u._id),
                        );
                      }}
                    />
                    <span>
                      {u.name}
                      {u.designation ? ` · ${u.designation}` : ""}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={assign}
            disabled={saving}
          >
            {saving ? "Saving…" : "Assign"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Main page ────────────────────────────────────────────── */
const AdminTasks = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const { user, isAccountsOrHr, isSuperAdmin } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [recurringSchedules, setRecurringSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTask, setEditTask] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [assignTask, setAssignTask] = useState(null);
  const [commentTask, setCommentTask] = useState(null);

  const [initFromUrl] = useState(() =>
    parseAdminTasksFromSearch(
      typeof window !== "undefined" ? window.location.search : "",
    ),
  );

  /* filters */
  const [search, setSearch] = useState(initFromUrl.search);
  const [fStatus, setFStatus] = useState(initFromUrl.fStatus);
  const [fPriority, setFPriority] = useState(initFromUrl.fPriority);
  const [fTag, setFTag] = useState(initFromUrl.fTag);
  const [fCreatedBy, setFCreatedBy] = useState(initFromUrl.fCreatedBy);
  const [fAssignedTo, setFAssignedTo] = useState(initFromUrl.fAssignedTo);
  const [fCoAssignedTo, setFCoAssignedTo] = useState(initFromUrl.fCoAssignedTo);
  const [createdFrom, setCreatedFrom] = useState(initFromUrl.createdFrom);
  const [createdTo, setCreatedTo] = useState(initFromUrl.createdTo);
  const [deadlineFrom, setDeadlineFrom] = useState(initFromUrl.deadlineFrom);
  const [deadlineTo, setDeadlineTo] = useState(initFromUrl.deadlineTo);
  const [showAdvanced, setShowAdvanced] = useState(initFromUrl.showAdvanced);
  const [quickFilter, setQuickFilter] = useState(initFromUrl.quickFilter);
  const [attachTask, setAttachTask] = useState(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const attachInputRef = React.useRef(null);
  const [taskTab, setTaskTab] = useState(initFromUrl.taskTab);

  /* pagination */
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(initFromUrl.page);

  /* sort */
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");

  /* Restore filters from URL (browser back, bookmarks) */
  useEffect(() => {
    const x = parseAdminTasksFromSearch(location.search);
    setTaskTab(x.taskTab);
    setSearch(x.search);
    setFStatus(x.fStatus);
    setFPriority(x.fPriority);
    setFCreatedBy(x.fCreatedBy);
    setFAssignedTo(x.fAssignedTo);
    setFCoAssignedTo(x.fCoAssignedTo);
    setFTag(x.fTag);
    setCreatedFrom(x.createdFrom);
    setCreatedTo(x.createdTo);
    setDeadlineFrom(x.deadlineFrom);
    setDeadlineTo(x.deadlineTo);
    setQuickFilter(x.quickFilter);
    setShowAdvanced(x.showAdvanced);
    setPage(x.page);
  }, [location.search]);

  const fetchAll = useCallback(async () => {
    const params = {};
    if (fStatus) params.status = fStatus;
    const [t, u, rs] = await Promise.all([
      api.get("/tasks/admin/all", { params }),
      api.get("/users"),
      api.get("/scheduled-tasks"),
    ]);
    setTasks(t.data.data);
    setUsers(u.data.data);
    setRecurringSchedules((rs.data.data || []).filter((x) => !!x.isRecurring));
    setLoading(false);
  }, [fStatus]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  /* Persist filters in URL (canonical compare — avoids sync loops / param order) */
  useEffect(() => {
    const stateSnapshot = {
      taskTab,
      search,
      fStatus,
      fPriority,
      fCreatedBy,
      fAssignedTo,
      fCoAssignedTo,
      fTag,
      createdFrom,
      createdTo,
      deadlineFrom,
      deadlineTo,
      quickFilter,
      showAdvanced,
      page,
    };
    const desired = serializeAdminTasksQuery(stateSnapshot);
    const curNorm = serializeAdminTasksQuery(
      parseAdminTasksFromSearch(location.search),
    );
    if (desired === curNorm) return;
    const nextObj = Object.fromEntries(new URLSearchParams(desired));
    setSearchParams(Object.keys(nextObj).length ? nextObj : {}, { replace: true });
  }, [
    taskTab,
    search,
    fStatus,
    fPriority,
    fCreatedBy,
    fAssignedTo,
    fCoAssignedTo,
    fTag,
    createdFrom,
    createdTo,
    deadlineFrom,
    deadlineTo,
    quickFilter,
    showAdvanced,
    page,
    location.search,
    setSearchParams,
  ]);

  const taskDetailReturnTo = `${location.pathname}${location.search}`;

  const handleSort = (f) => {
    if (sortBy === f) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(f);
      setSortDir("asc");
    }
  };
  const deleteTask = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    await api.delete(`/tasks/${id}`);
    toast.success("Task deleted");
    fetchAll();
  };

  const markTaskDoneFromTable = async (task) => {
    const me = user?._id ?? user?.id;
    if (!task || task.status === "done") return;
    if (!canMarkTaskComplete(me, task)) {
      toast.error(
        "Only the task creator or assigned users can mark this task as complete",
      );
      return;
    }
    try {
      const payload = {
        title: task.title,
        description: task.description,
        assignedTo: task.assignedTo?._id || undefined,
        assignedUsers: Array.isArray(task.assignedUsers)
          ? task.assignedUsers.map((u) => (u?._id ? u._id : u)).filter(Boolean)
          : task.assignedTo?._id
            ? [task.assignedTo._id]
            : [],
        status: "done",
        priority: task.priority,
        deadline: task.deadline,
        tags: task.tags || [],
      };
      const { data } = await api.put(`/tasks/${task._id}`, payload);
      if (!handlePendingDoneRequestResponse(data, toast)) {
        toast.success("Task marked as done");
      }
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update task");
    }
  };
  const toggleTaskSelected = (taskId, checked) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(taskId);
      else next.delete(taskId);
      return [...next];
    });
  };
  const toggleSelectAllCurrentPage = (checked, rows) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (checked) rows.forEach((t) => next.add(t._id));
      else rows.forEach((t) => next.delete(t._id));
      return [...next];
    });
  };
  const deleteSelectedTasks = async () => {
    if (selectedTaskIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedTaskIds.length} selected task(s)?`)) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/tasks/admin/bulk-delete", {
        taskIds: selectedTaskIds,
      });
      const deleted = data?.data?.deletedIds?.length || 0;
      const failed = data?.data?.failed?.length || 0;
      toast.success(
        `Deleted ${deleted} task(s)${failed ? `, ${failed} failed` : ""}`,
      );
      setSelectedTaskIds([]);
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  const sendWhatsAppReminder = async (task) => {
    const assigneeName = task.assignedTo?.name || "the assignee";
    try {
      await api.post(`/tasks/${task._id}/whatsapp-reminder`);
      toast.success(`WhatsApp reminder sent to ${assigneeName}`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to send WhatsApp reminder");
    }
  };
  const clearAll = () => {
    setSearch("");
    setFStatus("");
    setFPriority("");
    setFTag("");
    setFCreatedBy("");
    setFAssignedTo("");
    setFCoAssignedTo("");
    setCreatedFrom("");
    setCreatedTo("");
    setDeadlineFrom("");
    setDeadlineTo("");
    setQuickFilter("");
    setShowAdvanced(false);
    setPage(1);
  };

  const PRIO_ORD = { critical: 0, high: 1, medium: 2, low: 3 };
  const allTags = [...new Set(tasks.flatMap((t) => t.tags || []))].sort();
  const meId = user?._id || user?.id || null;
  const idOf = (val) => (val && typeof val === "object" ? val._id : val);
  const toIdString = (val) => {
    const id = idOf(val);
    return id != null ? String(id) : "";
  };
  const hasSeparateAssignee = (task) => {
    const createdById = toIdString(task?.createdBy);
    const assignedToId = toIdString(task?.assignedTo);
    return !!assignedToId && assignedToId !== createdById;
  };
  const displayAssignedUsersForTask = (task) => {
    const creatorId = toIdString(task?.createdBy);
    const multi = Array.isArray(task?.assignedUsers) && task.assignedUsers.length
      ? task.assignedUsers
      : task?.assignedTo
        ? [task.assignedTo]
        : [];
    return multi.filter((assignedUser) => toIdString(assignedUser) !== creatorId);
  };
  const createdByMe = meId
    ? tasks.filter((t) => toIdString(t.createdBy) === String(meId))
    : [];
  const recurringTasks = recurringSchedules;

  // Tab counts: for super admin, counts are global; for admin, counts are based on "created by me"
  const tabCounts = (() => {
    const pool = isSuperAdmin ? tasks : createdByMe;
    const assigned = pool.filter((t) => {
      const cid = toIdString(t.createdBy);
      const aid = toIdString(t.assignedTo);
      return !!aid && !!cid && aid !== cid;
    }).length;
    const self = pool.filter((t) => {
      const cid = toIdString(t.createdBy);
      const aid = toIdString(t.assignedTo);
      return !!aid && !!cid && aid === cid;
    }).length;
    const coAssigned = (isSuperAdmin ? tasks : tasks).filter(
      (t) => !!idOf(t.coAssignedTo),
    ).length;
    return {
      assigned,
      self,
      coAssigned,
      all: isSuperAdmin ? tasks.length : createdByMe.length,
    };
  })();

  const handleAttachClick = (task) => {
    setAttachTask(task);
    if (attachInputRef.current) {
      attachInputRef.current.value = "";
      attachInputRef.current.click();
    }
  };

  const handleAttachChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !attachTask) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/tasks/${attachTask._id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Attachment uploaded");
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to upload attachment");
    } finally {
      setAttachTask(null);
      if (attachInputRef.current) attachInputRef.current.value = "";
    }
  };

  /* client-side filter + sort */
  const baseTasks = [...tasks].filter((t) => {
    const cid = toIdString(t.createdBy);
    const aid = toIdString(t.assignedTo);

    // Done segregation:
    // - "Done Tasks" tab shows ONLY status === "done"
    // - every other tab hides status === "done"
    if (taskTab !== "recurring") {
      if (taskTab === "done") {
        if (t.status !== "done") return false;
      } else if (t.status === "done") {
        return false;
      }
    }

    // Recurring tab uses recurringSchedules table (not tasks)
    if (taskTab === "recurring") return true;

    // Co-assigned tab: any task with a co-assignee (global, no role restriction)
    if (taskTab === "coassigned") return !!idOf(t.coAssignedTo);

    // Super admin: tabs should work across ALL tasks
    if (isSuperAdmin) {
      if (taskTab === "assigned") return !!aid && !!cid && aid !== cid;
      if (taskTab === "self") return !!aid && !!cid && aid === cid;
      return true;
    }

    // Admin: tabs are based on tasks created by current user
    if (!meId) return true;
    if (taskTab === "assigned")
      return cid === String(meId) && !!aid && aid !== String(meId);
    if (taskTab === "self") return cid === String(meId);
    return true;
  });

  const displayed = baseTasks
    .filter((t) => {
      if (fPriority && t.priority !== fPriority) return false;
      if (fTag && !(t.tags || []).includes(fTag)) return false;
      if (fCreatedBy && toIdString(t.createdBy) !== String(fCreatedBy))
        return false;
      if (fAssignedTo && toIdString(t.assignedTo) !== String(fAssignedTo))
        return false;
      if (fCoAssignedTo && toIdString(t.coAssignedTo) !== String(fCoAssignedTo))
        return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !t.title.toLowerCase().includes(q) &&
          !t.assignedTo?.name?.toLowerCase().includes(q) &&
          !t.createdBy?.name?.toLowerCase().includes(q) &&
          !(t.tags || []).some((g) => g.toLowerCase().includes(q))
        )
          return false;
      }
      if (createdFrom || createdTo) {
        const c = new Date(t.createdAt);
        if (createdFrom && c < startOfDay(parseISO(createdFrom))) return false;
        if (createdTo && c > endOfDay(parseISO(createdTo))) return false;
      }
      if (deadlineFrom || deadlineTo) {
        if (!t.deadline) return false;
        const d = new Date(t.deadline);
        if (deadlineFrom && d < startOfDay(parseISO(deadlineFrom)))
          return false;
        if (deadlineTo && d > endOfDay(parseISO(deadlineTo))) return false;
      }
      if (quickFilter) {
        const today = new Date();
        const isSameDay = (a, b) => a.toDateString() === b.toDateString();
        if (quickFilter === "todo" && t.status !== "todo") return false;
        if (quickFilter === "in-progress" && t.status !== "in-progress")
          return false;
        if (quickFilter === "review" && t.status !== "review") return false;
        if (quickFilter === "done" && t.status !== "done") return false;
        if (quickFilter === "completed-today") {
          if (
            t.status !== "done" ||
            !t.updatedAt ||
            !isSameDay(new Date(t.updatedAt), today)
          ) {
            return false;
          }
        }
        if (quickFilter === "due-today") {
          if (
            !t.deadline ||
            t.status === "done" ||
            !isSameDay(new Date(t.deadline), today)
          ) {
            return false;
          }
        }
        if (quickFilter === "overdue") {
          if (
            !t.deadline ||
            t.status === "done" ||
            !(new Date(t.deadline) < today)
          ) {
            return false;
          }
        }
        if (quickFilter === "due-within-days") {
          const deadline = new Date(t.deadline);
          const in3Days = new Date(today);
          in3Days.setDate(in3Days.getDate() + 3);
          if (
            !t.deadline ||
            t.status === "done" ||
            deadline <= today ||
            deadline > in3Days
          ) {
            return false;
          }
        }
        if (quickFilter === "my-pending") {
          const aid = t.assignedTo?._id || t.assignedTo;
          // For admin view, "my" is not well-defined; use assignee-is-unfinished as workload
          if (t.status === "done" || !aid) return false;
        }
      }
      return true;
    })
    .sort((a, b) => {
      let vA, vB;
      if (sortBy === "createdAt") {
        vA = new Date(a.createdAt);
        vB = new Date(b.createdAt);
      } else if (sortBy === "deadline") {
        vA = a.deadline ? new Date(a.deadline) : new Date("9999");
        vB = b.deadline ? new Date(b.deadline) : new Date("9999");
      } else if (sortBy === "priority") {
        vA = PRIO_ORD[a.priority];
        vB = PRIO_ORD[b.priority];
      } else if (sortBy === "title") {
        vA = a.title.toLowerCase();
        vB = b.title.toLowerCase();
      } else if (sortBy === "status") {
        vA = a.status;
        vB = b.status;
      } else if (sortBy === "createdBy") {
        vA = (a.createdBy?.name || "").toLowerCase();
        vB = (b.createdBy?.name || "").toLowerCase();
      } else if (sortBy === "assignedTo") {
        const aAssigned = displayAssignedUsersForTask(a);
        const bAssigned = displayAssignedUsersForTask(b);
        vA = (
          aAssigned.length > 0
            ? aAssigned
                .map((u) => (u?.name || "").toLowerCase())
                .sort()
                .join(",")
            : "self created task"
        );
        vB = (
          bAssigned.length > 0
            ? bAssigned
                .map((u) => (u?.name || "").toLowerCase())
                .sort()
                .join(",")
            : "self created task"
        );
      } else if (sortBy === "coAssignedTo") {
        vA = (a.coAssignedTo?.name || "").toLowerCase();
        vB = (b.coAssignedTo?.name || "").toLowerCase();
      } else {
        vA = a[sortBy];
        vB = b[sortBy];
      }
      if (vA < vB) return sortDir === "asc" ? -1 : 1;
      if (vA > vB) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

  const totalPages = Math.max(1, Math.ceil(displayed.length / PAGE_SIZE));
  const pageStartIndex = (page - 1) * PAGE_SIZE;
  const currentPageTasks = displayed.slice(
    pageStartIndex,
    pageStartIndex + PAGE_SIZE,
  );
  const allCurrentPageSelected =
    currentPageTasks.length > 0 &&
    currentPageTasks.every((t) => selectedTaskIds.includes(t._id));

  /* reset page when filters/search change */
  useEffect(() => {
    setPage(1);
  }, [
    taskTab,
    search,
    fStatus,
    fPriority,
    fTag,
    fCreatedBy,
    fAssignedTo,
    fCoAssignedTo,
    createdFrom,
    createdTo,
    deadlineFrom,
    deadlineTo,
    quickFilter,
  ]);
  useEffect(() => {
    setSelectedTaskIds([]);
  }, [taskTab, search, fStatus, fPriority, fTag, page]);

  /* clamp page when filtered count shrinks */
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const advancedCount = [
    fTag,
    fCreatedBy,
    fAssignedTo,
    fCoAssignedTo,
    createdFrom,
    createdTo,
    deadlineFrom,
    deadlineTo,
  ].filter(Boolean).length;
  const byStatus = (s) => tasks.filter((t) => t.status === s).length;

  const userNameById = (id) => {
    if (!id) return "—";
    const match = users.find(
      (u) => String(u._id || u.id) === String(id),
    );
    return match?.name || "—";
  };

  const formatAssignedForExport = (task) => {
    const displayAssigned = displayAssignedUsersForTask(task);
    if (displayAssigned.length === 0) return "Self created task";
    return displayAssigned.map((u) => u?.name || "User").join(", ");
  };

  const buildExportFilterRows = (exportTotal, exportTotalPages) => [
    ["Export Information"],
    ["Exported At", format(new Date(), "MMM d, yyyy hh:mm a")],
    ["Tab", ADMIN_TASK_TAB_LABELS[taskTab] || taskTab],
    ["Search", search || "—"],
    ["Status Filter", fStatus ? STATUS_LABELS[fStatus] || fStatus : "All Statuses"],
    ["Priority Filter", fPriority ? fPriority : "All Priorities"],
    ["Tag Filter", fTag || "—"],
    ["Created By Filter", userNameById(fCreatedBy)],
    ["Assigned To Filter", userNameById(fAssignedTo)],
    ["Co-Assigned Filter", userNameById(fCoAssignedTo)],
    [
      "Created Date Range",
      createdFrom || createdTo
        ? `${createdFrom || "…"} to ${createdTo || "…"}`
        : "—",
    ],
    [
      "Deadline Range",
      deadlineFrom || deadlineTo
        ? `${deadlineFrom || "…"} to ${deadlineTo || "…"}`
        : "—",
    ],
    [
      "Quick Filter",
      quickFilter ? QUICK_FILTER_LABELS[quickFilter] || quickFilter : "—",
    ],
    ["Sort By", sortBy],
    ["Sort Direction", sortDir],
    [],
    ["Pagination Summary"],
    ["Page Size", PAGE_SIZE],
    ["Total Filtered Records", exportTotal],
    ["Total Pages", exportTotalPages],
    ["Current Page (at export)", page],
    [
      "Current Page Row Range",
      exportTotal === 0
        ? "—"
        : `${pageStartIndex + 1}-${Math.min(pageStartIndex + PAGE_SIZE, exportTotal)}`,
    ],
    ["Total Tasks Loaded", tasks.length],
    [],
  ];

  const handleExportExcel = () => {
    const stamp = format(new Date(), "yyyy-MM-dd");

    if (taskTab === "recurring") {
      const sortedRecurring = [...recurringSchedules].sort(
        (a, b) =>
          new Date(a.nextRunAt || a.scheduleDate) -
          new Date(b.nextRunAt || b.scheduleDate),
      );
      if (sortedRecurring.length === 0) {
        toast.error("No recurring tasks to export");
        return;
      }
      const recurringTotalPages = Math.max(
        1,
        Math.ceil(sortedRecurring.length / PAGE_SIZE),
      );
      const headers = [
        "Row #",
        "Page #",
        "Title",
        "Scheduled By",
        "Assigned To",
        "Next Run Date",
        "Next Run Time",
        "Priority",
      ];
      const dataRows = sortedRecurring.map((st, index) => {
        const runAt = st.nextRunAt || st.scheduleDate;
        const runDate = runAt ? new Date(runAt) : null;
        return [
          index + 1,
          Math.floor(index / PAGE_SIZE) + 1,
          st.title || "",
          st.createdBy?.name || "—",
          st.assignedTo?.name || "—",
          runDate ? format(runDate, "MMM d, yyyy") : "—",
          runDate ? format(runDate, "hh:mm a") : "—",
          st.priority || "—",
        ];
      });
      exportToCsv(`staff-recurring-tasks-${stamp}.csv`, [
        ...buildExportFilterRows(sortedRecurring.length, recurringTotalPages),
        ["Recurring Task Data"],
        headers,
        ...dataRows,
      ]);
      toast.success(`Exported ${sortedRecurring.length} recurring task(s)`);
      return;
    }

    if (displayed.length === 0) {
      toast.error("No tasks to export for the current filters");
      return;
    }

    const headers = [
      "Row #",
      "Page #",
      "Title",
      "Tags",
      "Created By",
      "Assigned To",
      "Co-Assigned",
      "Status",
      "Priority",
      "Created",
      "Deadline",
      "Overdue",
    ];
    const dataRows = displayed.map((t, index) => [
      index + 1,
      Math.floor(index / PAGE_SIZE) + 1,
      t.title || "",
      (t.tags || []).join(", "),
      t.createdBy?.name || "—",
      formatAssignedForExport(t),
      t.coAssignedTo?.name || "No co-assignee",
      STATUS_LABELS[t.status] || t.status || "—",
      t.priority || "—",
      t.createdAt
        ? format(new Date(t.createdAt), "MMM d, yyyy hh:mm a")
        : "—",
      t.deadline ? format(new Date(t.deadline), "MMM d, yyyy") : "—",
      isTaskOverdue(t) ? "Yes" : "No",
    ]);

    exportToCsv(`staff-tasks-${stamp}.csv`, [
      ...buildExportFilterRows(displayed.length, totalPages),
      ["Task Data"],
      headers,
      ...dataRows,
    ]);
    toast.success(`Exported ${displayed.length} task(s) to Excel`);
  };

  if (loading)
    return (
      <div className="loading">
        <div className="spinner" />
      </div>
    );

  return (
    <main className="page">
      <input
        ref={attachInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={handleAttachChange}
      />
      {(showModal || editTask) && (
        <TaskModal
          task={editTask}
          users={users}
          currentUserId={user?._id || user?.id}
          onClose={() => {
            setShowModal(false);
            setEditTask(null);
          }}
          onSaved={fetchAll}
        />
      )}
      {commentTask && (
        <CommentModal
          taskId={commentTask._id}
          taskTitle={commentTask.title}
          onClose={() => setCommentTask(null)}
        />
      )}
      {assignTask && (
        <AssignModal
          task={assignTask}
          users={users}
          onClose={() => setAssignTask(null)}
          onSaved={fetchAll}
        />
      )}

      {/* ── Header ── */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Task Management</h1>
          <p className="page-subtitle">
            {isAccountsOrHr
              ? "View and manage your own tasks only"
              : "View, create and assign tasks across all staff"}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <MdAdd size={18} /> Create Task
        </button>
      </div>

      {/* ── Stat cards ── */}
      <div className="grid grid-4" style={{ marginBottom: 24 }}>
        {[
          {
            label: "Total Tasks",
            value: tasks.length,
            color: "#6366f1",
            bg: "#e0e7ff",
          },
          {
            label: "To Do",
            value: byStatus("todo"),
            color: "#64748b",
            bg: "#f1f5f9",
          },
          {
            label: "In Progress",
            value: byStatus("in-progress"),
            color: "#3b82f6",
            bg: "#dbeafe",
          },
          {
            label: "Completed",
            value: byStatus("done"),
            color: "#10b981",
            bg: "#d1fae5",
          },
        ].map((s) => (
          <div key={s.label} className="card stat-card">
            <div className="stat-icon" style={{ background: s.bg }}>
              <MdTask color={s.color} size={22} />
            </div>
            <div className="stat-value">{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Tabs (segregate tasks) ── */}
      <div
        style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}
      >
        {[
          { id: "all", label: "All Tasks", count: tasks.length },
          { id: "self", label: "Self Created Tasks", count: tabCounts.self },
          { id: "done", label: "Done Tasks", count: byStatus("done") },
          {
            id: "assigned",
            label: "Tasks Assigned to Others",
            count: tabCounts.assigned,
          },
          {
            id: "coassigned",
            label: "Co-Assigned Tasks",
            count: tabCounts.coAssigned,
          },
          {
            id: "recurring",
            label: "Recurring Tasks",
            count: recurringTasks.length,
          },
        ].map((t) => {
          const active = taskTab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTaskTab(t.id)}
              className={`admin-task-tab admin-task-tab--${t.id} ${
                active ? "is-active" : ""
              }`}
            >
              <span>{t.label}</span>
              <span className="admin-task-tab-count">{t.count}</span>
            </button>
          );
        })}
      </div>

      {/* ── Primary filter bar ── */}
      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: showAdvanced ? 12 : 20,
        }}
      >
        {taskTab !== "recurring" && (
          <button
            className="btn btn-danger"
            onClick={deleteSelectedTasks}
            disabled={bulkDeleting || selectedTaskIds.length === 0}
          >
            {bulkDeleting
              ? "Deleting..."
              : `Delete Selected (${selectedTaskIds.length})`}
          </button>
        )}
        {/* Search */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: "var(--surface)",
            border: "1.5px solid var(--border)",
            borderRadius: 10,
            padding: "0 14px",
            flex: 1,
            maxWidth: 320,
          }}
        >
          <MdSearch color="var(--text-muted)" size={17} />
          <input
            style={{
              border: "none",
              outline: "none",
              padding: "9px 0",
              fontSize: "0.875rem",
              width: "100%",
              background: "transparent",
              color: "var(--text)",
            }}
            placeholder="Search tasks, staff, tags…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                display: "flex",
                color: "var(--text-muted)",
              }}
            >
              <MdClose size={15} />
            </button>
          )}
        </div>

        <select
          className="form-control"
          style={{ width: "auto" }}
          value={fStatus}
          onChange={(e) => setFStatus(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="todo">To Do</option>
          <option value="in-progress">In Progress</option>
          <option value="review">Review</option>
          <option value="done">Done</option>
        </select>

        <select
          className="form-control"
          style={{ width: "auto" }}
          value={fPriority}
          onChange={(e) => setFPriority(e.target.value)}
        >
          <option value="">All Priorities</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="critical">Critical</option>
        </select>

        {/* Advanced toggle */}
        <button
          className={`btn btn-sm ${showAdvanced ? "btn-primary" : "btn-secondary"}`}
          onClick={() => setShowAdvanced((v) => !v)}
          style={{ position: "relative", gap: 6 }}
        >
          <MdFilterAlt size={15} />
          Filters
          {advancedCount > 0 && (
            <span
              style={{
                position: "absolute",
                top: -7,
                right: -7,
                background: "var(--danger)",
                color: "#fff",
                borderRadius: "50%",
                width: 18,
                height: 18,
                fontSize: "0.62rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                border: "2px solid var(--surface)",
              }}
            >
              {advancedCount}
            </span>
          )}
        </button>

        {(search || fStatus || fPriority || advancedCount > 0) && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={clearAll}
            style={{ gap: 5 }}
          >
            <MdClear size={14} /> Clear All
          </button>
        )}

        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={handleExportExcel}
          disabled={
            taskTab === "recurring"
              ? recurringSchedules.length === 0
              : displayed.length === 0
          }
          style={{ gap: 5, marginLeft: "auto" }}
        >
          <MdFileDownload size={15} />
          Export Excel
        </button>

        <span
          style={{
            fontSize: "0.82rem",
            color: "var(--text-muted)",
            fontWeight: 600,
          }}
        >
          {taskTab === "recurring"
            ? `${recurringSchedules.length} recurring`
            : `${displayed.length} / ${tasks.length} tasks`}
        </span>
      </div>

      {/* ── Advanced filter panel ── */}
      {showAdvanced && (
        <div
          className="card"
          style={{
            padding: "18px 20px",
            marginBottom: 20,
            borderColor: "var(--primary)",
            background: "var(--primary-light)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 14,
            }}
          >
            <span
              style={{
                fontWeight: 700,
                fontSize: "0.78rem",
                textTransform: "uppercase",
                letterSpacing: ".7px",
                color: "var(--primary)",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <MdFilterAlt size={15} /> Advanced Filters
            </span>
            <button
              className="btn-icon"
              style={{ padding: 4 }}
              onClick={() => setShowAdvanced(false)}
            >
              <MdClose size={16} />
            </button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 14,
            }}
          >
            {/* Created By */}
            {!isAccountsOrHr && (
              <div>
                <label
                  className="form-label"
                  style={{ display: "flex", alignItems: "center", gap: 5 }}
                >
                  <MdPerson size={13} /> Created By
                </label>
                <select
                  className="form-control"
                  value={fCreatedBy}
                  onChange={(e) => { setFCreatedBy(e.target.value); setPage(1); }}
                >
                  <option value="">All Creators</option>
                  {users.map((u) => (
                    <option key={u._id} value={u._id}>{u.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Assigned To */}
            {!isAccountsOrHr && (
              <div>
                <label
                  className="form-label"
                  style={{ display: "flex", alignItems: "center", gap: 5 }}
                >
                  <MdPerson size={13} /> Assigned To
                </label>
                <select
                  className="form-control"
                  value={fAssignedTo}
                  onChange={(e) => { setFAssignedTo(e.target.value); setPage(1); }}
                >
                  <option value="">All Assignees</option>
                  {users.map((u) => (
                    <option key={u._id} value={u._id}>{u.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Co-Assigned To */}
            {!isAccountsOrHr && (
              <div>
                <label
                  className="form-label"
                  style={{ display: "flex", alignItems: "center", gap: 5 }}
                >
                  <MdPerson size={13} /> Co-Assigned To
                </label>
                <select
                  className="form-control"
                  value={fCoAssignedTo}
                  onChange={(e) => { setFCoAssignedTo(e.target.value); setPage(1); }}
                >
                  <option value="">All Co-Assignees</option>
                  {users.map((u) => (
                    <option key={u._id} value={u._id}>{u.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Tag */}
            <div>
              <label
                className="form-label"
                style={{ display: "flex", alignItems: "center", gap: 5 }}
              >
                <MdLabel size={13} /> Tag
              </label>
              <select
                className="form-control"
                value={fTag}
                onChange={(e) => setFTag(e.target.value)}
              >
                <option value="">All Tags</option>
                {allTags.map((tag) => (
                  <option key={tag} value={tag}>
                    {tag}
                  </option>
                ))}
              </select>
            </div>

            {/* Created date range */}
            <div>
              <label
                className="form-label"
                style={{ display: "flex", alignItems: "center", gap: 5 }}
              >
                <MdDateRange size={13} /> Created From
              </label>
              <input
                className="form-control"
                type="date"
                value={createdFrom}
                onChange={(e) => setCreatedFrom(e.target.value)}
              />
            </div>
            <div>
              <label
                className="form-label"
                style={{ display: "flex", alignItems: "center", gap: 5 }}
              >
                <MdDateRange size={13} /> Created To
              </label>
              <input
                className="form-control"
                type="date"
                value={createdTo}
                onChange={(e) => setCreatedTo(e.target.value)}
              />
            </div>

            {/* Deadline range */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
              }}
            >
              <div>
                <label className="form-label">Deadline From</label>
                <input
                  className="form-control"
                  type="date"
                  value={deadlineFrom}
                  onChange={(e) => setDeadlineFrom(e.target.value)}
                />
              </div>
              <div>
                <label className="form-label">Deadline To</label>
                <input
                  className="form-control"
                  type="date"
                  value={deadlineTo}
                  onChange={(e) => setDeadlineTo(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      <div className="card" style={{ overflow: "hidden" }}>
        {taskTab === "recurring" ? (
          recurringSchedules.length === 0 ? (
            <div className="empty-state">
              <MdSchedule size={52} />
              <h3>No recurring schedules found</h3>
              <p>Create a recurring schedule from Scheduled Tasks.</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Scheduled By</th>
                    <th>Assigned To</th>
                    <th>Next Run</th>
                    <th>Priority</th>
                    <th style={{ textAlign: "center", width: 52 }}>•••</th>
                  </tr>
                </thead>
                <tbody>
                  {recurringSchedules
                    .slice()
                    .sort(
                      (a, b) =>
                        new Date(a.nextRunAt || a.scheduleDate) -
                        new Date(b.nextRunAt || b.scheduleDate),
                    )
                    .slice(pageStartIndex, pageStartIndex + PAGE_SIZE)
                    .map((st) => {
                      const runAt = st.nextRunAt || st.scheduleDate;
                      return (
                        <tr key={st._id}>
                          <td style={{ minWidth: 180, fontWeight: 600 }}>
                            {st.title}
                          </td>
                          <td>{st.createdBy?.name || "—"}</td>
                          <td>{st.assignedTo?.name || "—"}</td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <div
                              style={{ fontSize: "0.82rem", fontWeight: 600 }}
                            >
                              {format(new Date(runAt), "MMM d, yyyy")}
                            </div>
                            <div
                              style={{
                                fontSize: "0.7rem",
                                color: "var(--text-muted)",
                              }}
                            >
                              {format(new Date(runAt), "hh:mm a")}
                            </div>
                          </td>
                          <td>
                            <span className={`badge badge-${st.priority}`}>
                              {st.priority}
                            </span>
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <ActionMenu
                              items={[
                                {
                                  label: "View schedule task",
                                  icon: <MdVisibility size={15} />,
                                  onClick: () =>
                                    navigate(`/scheduled-tasks/${st._id}`),
                                },
                                {
                                  label: "Edit",
                                  icon: <MdEdit size={15} />,
                                  onClick: () =>
                                    navigate(`/scheduled-tasks/${st._id}/edit`),
                                },
                                "divider",
                                {
                                  label: "Delete",
                                  icon: <MdDelete size={15} />,
                                  onClick: async () => {
                                    if (
                                      !window.confirm(
                                        "Delete this recurring schedule?",
                                      )
                                    )
                                      return;
                                    await api.delete(
                                      `/scheduled-tasks/${st._id}`,
                                    );
                                    toast.success("Schedule deleted");
                                    fetchAll();
                                  },
                                  danger: true,
                                },
                              ]}
                            />
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )
        ) : displayed.length === 0 ? (
          <div className="empty-state">
            <MdTask size={52} />
            <h3>No tasks found</h3>
            <p>Adjust your filters or create a new task.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 40, textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={allCurrentPageSelected}
                      onChange={(e) =>
                        toggleSelectAllCurrentPage(e.target.checked, currentPageTasks)
                      }
                      title="Select all tasks on this page"
                    />
                  </th>
                  <SortTH
                    label="Title"
                    field="title"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortTH
                    label="Created By"
                    field="createdBy"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortTH
                    label="Assigned To"
                    field="assignedTo"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortTH
                    label="Co-Assigned"
                    field="coAssignedTo"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortTH
                    label="Status"
                    field="status"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortTH
                    label="Priority"
                    field="priority"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortTH
                    label="Created"
                    field="createdAt"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortTH
                    label="Deadline"
                    field="deadline"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <th style={{ textAlign: "center", width: 52 }}>•••</th>
                </tr>
              </thead>
              <tbody>
                {currentPageTasks.map((t) => (
                  <tr
                    key={t._id}
                    className={isTaskOverdue(t) ? "admin-task-row--overdue" : undefined}
                  >
                    <td style={{ textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={selectedTaskIds.includes(t._id)}
                        onChange={(e) =>
                          toggleTaskSelected(t._id, e.target.checked)
                        }
                      />
                    </td>
                    {/* Title + tags */}
                    <td style={{ minWidth: 180 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          maxWidth: 220,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Link
                          to={`/tasks/${t._id}`}
                          state={{ returnTo: taskDetailReturnTo }}
                          className="task-list-title-link"
                          style={{
                            color: "var(--text)",
                            textDecoration: "none",
                          }}
                        >
                          {t.title}
                        </Link>
                      </div>
                      {t.tags?.length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            gap: 3,
                            marginTop: 4,
                            flexWrap: "wrap",
                          }}
                        >
                          {t.tags.map((tag) => (
                            <span
                              key={tag}
                              onClick={() => setFTag(tag === fTag ? "" : tag)}
                              style={{
                                fontSize: "0.62rem",
                                background: "var(--primary-light)",
                                color: "var(--primary)",
                                padding: "2px 7px",
                                borderRadius: 99,
                                fontWeight: 600,
                                cursor: "pointer",
                              }}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>

                    {/* Created By — real photo */}
                    <td>
                      {t.createdBy ? (
                        <button
                          type="button"
                          className="admin-task-user-filterhit"
                          title="Filter by creator"
                          onClick={() => {
                            const id = idOf(t.createdBy);
                            setFCreatedBy((prev) => (prev === id ? "" : id));
                            setShowAdvanced(true);
                            setPage(1);
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <UserAvatar user={t.createdBy} size={38} />
                            <span
                              style={{
                                fontSize: "0.82rem",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {t.createdBy.name}
                            </span>
                          </div>
                        </button>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>

                    {/* Assigned To — overlapping avatars + comma names */}
                    <td>
                      {(() => {
                        const displayAssigned = displayAssignedUsersForTask(t);
                        if (displayAssigned.length === 0) {
                          return (
                            <button
                              type="button"
                              className="admin-task-user-filterhit"
                              title="Filter tasks assigned to this person (self-created)"
                              onClick={() => {
                                const id = idOf(t.createdBy);
                                if (!id) return;
                                setFAssignedTo((prev) => (prev === id ? "" : id));
                                setShowAdvanced(true);
                                setPage(1);
                              }}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "4px 10px",
                                borderRadius: 999,
                                background: "var(--primary-light)",
                                color: "var(--primary)",
                                fontSize: "0.72rem",
                                fontWeight: 700,
                                textTransform: "capitalize",
                                whiteSpace: "nowrap",
                                border: "none",
                                cursor: "pointer",
                                fontFamily: "inherit",
                              }}
                            >
                              self created task
                            </button>
                          );
                        }
                        return (
                          <button
                            type="button"
                            className="admin-task-user-filterhit"
                            title="Filter by first assignee"
                            onClick={() => {
                              const id = idOf(displayAssigned[0]);
                              setFAssignedTo((prev) => (prev === id ? "" : id));
                              setShowAdvanced(true);
                              setPage(1);
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "center" }}>
                                {displayAssigned.slice(0, 4).map((assignedUser, idx) => (
                                  <div
                                    key={idOf(assignedUser)}
                                    style={{
                                      marginLeft: idx === 0 ? 0 : -10,
                                      border: "2px solid var(--surface)",
                                      borderRadius: "50%",
                                    }}
                                  >
                                    <UserAvatar user={assignedUser} size={30} />
                                  </div>
                                ))}
                              </div>
                              <span
                                style={{
                                  fontSize: "0.82rem",
                                  fontWeight: 600,
                                  whiteSpace: "normal",
                                }}
                              >
                                {displayAssigned
                                  .map((u) => (u?.name || "User").trim().split(/\s+/)[0] || "User")
                                  .join(", ")}
                              </span>
                            </div>
                          </button>
                        );
                      })()}
                    </td>

                    {/* Co-Assigned — dedicated column */}
                    <td>
                      {t.coAssignedTo ? (
                        <button
                          type="button"
                          className="admin-task-user-filterhit"
                          title="Filter by co-assignee"
                          onClick={() => {
                            const id = idOf(t.coAssignedTo);
                            setFCoAssignedTo((prev) => (prev === id ? "" : id));
                            setShowAdvanced(true);
                            setPage(1);
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <UserAvatar user={t.coAssignedTo} size={34} />
                            <span
                              style={{
                                fontSize: "0.82rem",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {t.coAssignedTo.name}
                            </span>
                          </div>
                        </button>
                      ) : (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "3px 10px",
                            borderRadius: 999,
                            background: "#f1f5f9",
                            color: "#94a3b8",
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            border: "1px dashed #cbd5e1",
                          }}
                        >
                          no co-assignee
                        </span>
                      )}
                    </td>

                    <td>
                      <span className={`badge badge-${t.status}`}>
                        {t.status}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-${t.priority}`}>
                        {t.priority}
                      </span>
                    </td>

                    <td style={{ whiteSpace: "nowrap" }}>
                      <div style={{ fontSize: "0.82rem", fontWeight: 600 }}>
                        {format(new Date(t.createdAt), "MMM d, yyyy")}
                      </div>
                      <div
                        style={{
                          fontSize: "0.7rem",
                          color: "var(--text-muted)",
                        }}
                      >
                        {format(new Date(t.createdAt), "hh:mm a")}
                      </div>
                    </td>

                    <td style={{ whiteSpace: "nowrap" }}>
                      {t.deadline ? (
                        <div style={{ fontSize: "0.82rem", fontWeight: 600 }}>
                          {format(new Date(t.deadline), "MMM d, yyyy")}
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>

                    {/* ── 3-dot action menu ── */}
                    <td style={{ textAlign: "center" }}>
                      <ActionMenu
                        items={[
                          {
                            label: "View task details",
                            icon: <MdVisibility size={15} />,
                            onClick: () =>
                              navigate(`/tasks/${t._id}`, {
                                state: { returnTo: taskDetailReturnTo },
                              }),
                          },
                          {
                            label: "View / add comments",
                            icon: <MdComment size={15} />,
                            onClick: () => setCommentTask(t),
                          },
                          {
                            label: "Attach file",
                            icon: <MdAttachFile size={15} />,
                            onClick: () => handleAttachClick(t),
                          },
                          {
                            label: "Assign task",
                            icon: <MdAssignment size={15} />,
                            onClick: () => setAssignTask(t),
                          },
                          {
                            label: "Edit task",
                            icon: <MdEdit size={15} />,
                            onClick: () => setEditTask(t),
                          },
                          ...(t.status !== "done" &&
                          canMarkTaskComplete(user?._id || user?.id, t)
                            ? [
                                {
                                  label: "Mark task as done",
                                  icon: <MdCheckCircle size={15} />,
                                  onClick: () => markTaskDoneFromTable(t),
                                },
                              ]
                            : []),
                          ...(t.assignedTo?._id && t.assignedTo._id !== t.createdBy?._id
                            ? [{
                                label: (
                                  <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.35 }}>
                                    <span>Send WhatsApp reminder</span>
                                    <span style={{ fontSize: "0.75rem", color: "#25d366", fontWeight: 600 }}>
                                      to {t.assignedTo.name}
                                    </span>
                                  </span>
                                ),
                                icon: <MdWhatsapp size={15} color="#25d366" />,
                                onClick: () => sendWhatsAppReminder(t),
                              }]
                            : []),
                          "divider",
                          {
                            label: "Delete task",
                            icon: <MdDelete size={15} />,
                            onClick: () => deleteTask(t._id),
                            danger: true,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Pagination controls */}
      {displayed.length > 0 && (
        <div
          style={{
            marginTop: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            fontSize: "0.8rem",
          }}
        >
          <span style={{ color: "var(--text-muted)" }}>
            Showing{" "}
            <strong>
              {pageStartIndex + 1}-
              {Math.min(pageStartIndex + PAGE_SIZE, displayed.length)}
            </strong>{" "}
            of <strong>{displayed.length}</strong> filtered tasks
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              className="btn btn-sm btn-secondary"
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                type="button"
                className="btn btn-sm"
                onClick={() => setPage(p)}
                style={{
                  minWidth: 32,
                  paddingInline: 10,
                  fontWeight: 600,
                  background: p === page ? "var(--primary)" : "var(--surface2)",
                  color: p === page ? "#fff" : "var(--text)",
                  borderColor: p === page ? "var(--primary)" : "var(--border)",
                }}
              >
                {p}
              </button>
            ))}
            <button
              className="btn btn-sm btn-secondary"
              type="button"
              disabled={page >= totalPages}
              onClick={() =>
                setPage((p) => (p < totalPages ? p + 1 : totalPages))
              }
            >
              Next
            </button>
          </div>
        </div>
      )}
    </main>
  );
};

export default AdminTasks;
